import { useState, createContext, useRef } from 'react';

import ModalConfirm from '../components/modal/ModalConfirm';
import ModalAlert from '../components/modal/ModalAlert';
import ModalAlertOrigin from '../components/modal/ModalAlertOrigin';
import ModalCheck from '../components/modal/ModalCheck';
import ModalCalendar from '../components/modal/ModalCalendar';
import ModalCalendarStudent from '../components/modal/ModalCalendarStudent';

import { confirm_buttons, confirm_contents, alert_contents, check_contents, setTitle } from '../utils/modal_contents';

// ========================================================================================
// Type 설정
interface ModalConfirmDataType {
  visible: boolean;
  title: string;
  left: string;
  right: string;
  button_type: string;
  confirm_callback: () => void;
  cancle_callback?: () => void;
  payment_callback?: () => void;
}

interface ModalAlertDataType {
  visible: boolean;
  title: string;
  timeout: number | undefined;
  use_close_button?: boolean;
}
interface ModalCheckDataType {
  visible: boolean;
  title: string;
}

interface ModalCalendarDataType {
  visible: boolean;
  title: string;
  type: string;
  date: {
    start: Date | null;
    end: Date | null;
    [key: string]: Date | null;
  };
  default?: Date;
  max: Date;
  min: Date | null;
}

interface ModalCalendarYearDataType extends ModalCalendarDataType {
  openTo: 'year' | 'month' | 'day';
  time: boolean;
  key?: string;
  callback?: () => void;
}

interface ModalControllerType {
  // Modal Confrim
  root_visible: boolean;
  setRootState: (visible: boolean) => void;
  modal_confirm: {
    data: ModalConfirmDataType;
    closeModalConfirm: () => void;
    openModalConfirm: (
      button_type: string,
      type: string,
      confirm_callback: () => void,
      cancle_callback?: () => void,
      target?: string,
      payment_callback?: () => void,
    ) => void;
  };
  modal_alert: {
    data: ModalAlertDataType;
    closeModalAlert: () => void;
    openModalAlert: (
      type: string,
      target?: string,
      callback?: () => void,
      manualClose?: boolean,
      target2?: string,
      useCloseButton?: boolean,
    ) => void;
  };
  modal_alert_origin: {
    data: ModalAlertDataType;
    closeModalAlertOrigin: () => void;
    openModalAlertOrigin: (
      type: string,
      target?: string,
      callback?: () => void,
      manualClose?: boolean,
      target2?: string,
      useCloseButton?: boolean,
    ) => void;
  };
  modal_check: {
    data: ModalCheckDataType;
    closeModalCheck: () => void;
    openModalCheck: (type: string) => void;
  };
  modal_calendar: {
    data: ModalCalendarDataType;
    closeModalCalendar: () => void;
    openModalCalendar: (type: string, value?: Date | null, max?: Date, min?: Date, default_month?: Date) => void;
    setModalCalendarDataDate: (date_arr: Date[]) => void;
    setModalCalendarDataDateType: (date: Date, type: string) => void;
    setModalCalendarDataDateClear: () => void;
  };
  modal_calendar_year: {
    data: ModalCalendarYearDataType;
    closeModalCalendar: () => void;
    openModalCalendar: (
      type: string,
      value?: Date | null,
      max?: Date,
      min?: Date,
      default_month?: Date,
      openTo?: 'year' | 'month' | 'day',
      time?: boolean,
      key?: string,
      callback?: () => void,
    ) => void;
    setModalCalendarDataDate: (date_arr: Date[]) => void;
    setModalCalendarDataDateType: (date: Date, type: string) => void;
    setModalCalendarDataDateClear: () => void;
  };
}

// ========================================================================================
// 초기값 설정
export const ModalContext = createContext<ModalControllerType>({
  root_visible: false,
  setRootState: (visible: boolean) => {
    return;
  },
  modal_confirm: {
    data: {
      visible: false,
      title: '',
      left: '',
      right: '',
      button_type: '',
      confirm_callback: () => {
        return;
      },
      cancle_callback: () => {
        return;
      },
      payment_callback: () => {
        return;
      },
    },
    closeModalConfirm: () => {
      return;
    },
    openModalConfirm: (
      button_type: string,
      type: string,
      confirm_callback: () => void,
      cancle_callback?: () => void,
      target?: string,
      payment_callback?: () => void,
    ) => {
      return;
    },
  },
  modal_alert: {
    data: {
      visible: false,
      title: '',
      timeout: undefined,
    },
    closeModalAlert: () => {
      return;
    },
    openModalAlert: (type: string) => {
      return;
    },
  },
  modal_alert_origin: {
    data: {
      visible: false,
      title: '',
      timeout: undefined,
    },
    closeModalAlertOrigin: () => {
      return;
    },
    openModalAlertOrigin: (type: string) => {
      return;
    },
  },
  modal_check: {
    data: {
      visible: false,
      title: '',
    },
    closeModalCheck: () => {
      return;
    },
    openModalCheck: (type: string) => {
      return;
    },
  },
  modal_calendar: {
    data: {
      visible: false,
      title: '',
      type: '',
      date: {
        start: new Date(),
        end: new Date(),
        reservation: new Date(),
      },
      max: new Date(),
      min: null,
    },
    closeModalCalendar: () => {
      return;
    },
    openModalCalendar: (type: string, value?: Date | null, max?: Date, min?: Date, default_month?: Date) => {
      return;
    },
    setModalCalendarDataDate: (date_arr: Date[]) => {
      return;
    },
    setModalCalendarDataDateType: (date: Date, type: string) => {
      return;
    },
    setModalCalendarDataDateClear: () => {
      return;
    },
  },
  modal_calendar_year: {
    data: {
      visible: false,
      title: '',
      type: '',
      openTo: 'year',
      date: {
        start: new Date(),
        end: new Date(),
      },
      max: new Date(),
      min: null,
      time: false,
      key: undefined,
      callback: undefined,
    },
    closeModalCalendar: () => {
      return;
    },
    openModalCalendar: (
      type: string,
      value?: Date | null,
      max?: Date,
      min?: Date,
      default_month?: Date,
      openTo?: string,
      time?: boolean,
      key?: string,
      callback?: () => void,
    ) => {
      return;
    },
    setModalCalendarDataDate: (date_arr: Date[]) => {
      return;
    },
    setModalCalendarDataDateType: (date: Date, type: string) => {
      return;
    },
    setModalCalendarDataDateClear: () => {
      return;
    },
  },
});

// ========================================================================================
// Provider 설정

function ModalProvider(props: { children: React.ReactNode }) {
  const children = props.children;

  // modalController 객체 안에 넣으면
  // 실시간 렌더 x
  // useState 사용 바람
  const [rootVisible, setRootVisible] = useState(false);
  const [modalConfirmData, setModalConfirmData] = useState<ModalConfirmDataType>({
    visible: false,
    title: '',
    left: '',
    right: '',
    button_type: '',
    confirm_callback: () => {
      return;
    },
    cancle_callback: () => {
      return;
    },
    payment_callback: () => {
      return;
    },
  });

  const [modalAlertData, setModalAlertData] = useState<ModalAlertDataType>({
    visible: false,
    title: '',
    timeout: undefined,
    use_close_button: false,
  });
  const [modalAlertOriginData, setModalAlertOriginData] = useState<ModalAlertDataType>({
    visible: false,
    title: '',
    timeout: undefined,
    use_close_button: false,
  });
  const alert_timeout = useRef<number | undefined>(undefined);
  alert_timeout.current = modalAlertData.timeout;

  const alert_origin_timeout = useRef<number | undefined>(undefined);
  alert_origin_timeout.current = modalAlertOriginData.timeout;

  const [modalCheckData, setModalCheckData] = useState<ModalCheckDataType>({
    visible: false,
    title: '',
  });

  const [modalCalendarData, setModalCalendarData] = useState<ModalCalendarDataType>({
    visible: false,
    title: '',
    type: '',
    date: {
      start: new Date(),
      end: new Date(),
      reservation: new Date(),
    },
    default: undefined,
    max: new Date(),
    min: null,
  });

  const [modalCalendarYearData, setModalCalendarYearData] = useState<ModalCalendarYearDataType>({
    visible: false,
    openTo: 'year',
    title: '',
    type: '',
    date: {
      start: new Date(),
      end: new Date(),
    },
    max: new Date(),
    min: null,
    time: false,
    key: '',
  });

  // 모달 컨트롤러
  const modalController: ModalControllerType = {
    root_visible: rootVisible,
    setRootState: (visible: boolean) => {
      setRootVisible(visible);
    },
    modal_confirm: {
      data: modalConfirmData,
      closeModalConfirm: () => {
        setModalConfirmData({
          title: '',
          visible: false,
          left: '',
          right: '',
          button_type: '',
          confirm_callback: () => {
            return;
          },
          cancle_callback: () => {
            return;
          },
          payment_callback: () => {
            return;
          },
        });
      },
      openModalConfirm: (
        button_type: string,
        type: string,
        confirm_callback: () => void,
        cancle_callback?: () => void,
        target?: string,
        payment_callback?: () => void,
      ) => {
        const title_data = {
          title: confirm_contents[type].title,
          sub_title: confirm_contents[type].sub_title ? confirm_contents[type].sub_title : null,
          target: target ? target : null,
        };

        setModalConfirmData({
          title: setTitle(title_data),
          visible: true,
          left: confirm_buttons[button_type][0],
          right: confirm_buttons[button_type][1],
          button_type,
          confirm_callback: confirm_callback,
          cancle_callback: cancle_callback,
          payment_callback: payment_callback,
        });
      },
    },
    modal_alert: {
      data: modalAlertData,
      closeModalAlert: () => {
        setModalAlertData(prev => ({
          ...prev,
          visible: false,
          timeout: undefined,
        }));
        if (alert_timeout.current != undefined) {
          clearTimeout(alert_timeout.current);
        }
      },
      openModalAlert: (
        type: string,
        target?: string,
        callback?: () => void,
        manualClose?: boolean,
        target2?: string,
        useCloseButton?: boolean,
      ) => {
        const title_data = {
          title: alert_contents[type].title,
          sub_title: alert_contents[type].sub_title ? alert_contents[type].sub_title : null,
          target: target ? target : null,
          target2: target2 ? target2 : undefined,
        };
        setModalAlertData(state => {
          if (state.timeout) {
            clearTimeout(state.timeout);
          }

          return {
            visible: true,
            title: setTitle(title_data),
            use_close_button: useCloseButton === false ? false : true,
            timeout: manualClose
              ? undefined
              : window.setTimeout(
                  () => {
                    modalController.modal_alert.closeModalAlert();
                    if (callback) callback();
                  },

                  ['success_regi_account_history_wait', 'success_regi_account_history_with_cancel'].includes(type)
                    ? 4000
                    : 2000,
                ),
          };
        });
      },
    },
    modal_alert_origin: {
      data: modalAlertOriginData,
      closeModalAlertOrigin: () => {
        setModalAlertOriginData(prev => ({
          ...prev,
          visible: false,
          timeout: undefined,
        }));
        if (alert_origin_timeout.current != undefined) {
          clearTimeout(alert_origin_timeout.current);
        }
      },
      openModalAlertOrigin: (
        type: string,
        target?: string,
        callback?: () => void,
        manualClose?: boolean,
        target2?: string,
        useCloseButton?: boolean,
      ) => {
        const title_data = {
          title: alert_contents[type].title,
          sub_title: alert_contents[type].sub_title ? alert_contents[type].sub_title : null,
          target: target ? target : null,
          target2: target2 ? target2 : undefined,
        };
        setModalAlertOriginData(state => {
          if (state.timeout) {
            clearTimeout(state.timeout);
          }

          return {
            visible: true,
            title: setTitle(title_data),
            use_close_button: useCloseButton === false ? false : true,
            timeout: manualClose
              ? undefined
              : window.setTimeout(
                  () => {
                    modalController.modal_alert_origin.closeModalAlertOrigin();
                    if (callback) callback();
                  },

                  ['success_regi_account_history_wait', 'success_regi_account_history_with_cancel'].includes(type)
                    ? 4000
                    : 2000,
                ),
          };
        });
      },
    },
    modal_check: {
      data: modalCheckData,
      closeModalCheck: () => {
        setModalCheckData({ visible: false, title: '' });
      },
      openModalCheck: (type: string) => {
        const title_data = {
          title: check_contents[type].title,
          sub_title: check_contents[type].sub_title ? check_contents[type].sub_title : null,
          target: null,
        };
        setModalCheckData({ visible: true, title: setTitle(title_data) });
      },
    },
    modal_calendar: {
      data: modalCalendarData,
      closeModalCalendar: () => {
        setModalCalendarData(state => {
          return { ...state, visible: false, default: undefined };
        });
      },
      openModalCalendar: (type: string, value?: Date | null, max?: Date, min?: Date, default_month?: Date) => {
        setModalCalendarData(state => {
          return {
            ...state,
            visible: true,
            type: type,
            date: {
              ...state.date,
              [type]: value ? value : null,
            },
            default: default_month,
            max: max ? max : new Date('9999-12-12'),
            min: min ? min : null,
          };
        });
      },
      setModalCalendarDataDate: (date_arr: Date[]) => {
        // const data = setAgoDate(date_type, gap);
        setModalCalendarData(state => {
          return {
            ...state,
            date: {
              start: date_arr[0],
              end: date_arr[1],
            },
          };
        });
      },
      setModalCalendarDataDateType: (date: Date, type: string) => {
        setModalCalendarData(state => {
          return {
            ...state,
            date: {
              ...state.date,
              [type]: date,
            },
          };
        });
      },
      setModalCalendarDataDateClear: () => {
        setModalCalendarData(state => {
          return {
            ...state,
            visible: false,
            date: {
              ...state.date,
              useDetailStart: null,
              useDetailEnd: null,
              allCheckStart: null,
              allCheckEnd: null,
              chargeDetailStart: null,
              chargeDetailEnd: null,
              checkStart: null,
              checkEnd: null,
              smsDetailStart: null,
              smsDetailEnd: null,
              smsReservationStart: null,
              smsReservationEnd: null,
            },
          };
        });
      },
    },
    modal_calendar_year: {
      data: modalCalendarYearData,
      closeModalCalendar: () => {
        setModalCalendarYearData(state => {
          return { ...state, visible: false };
        });
      },
      openModalCalendar: (
        type: string,
        value?: Date | null,
        max?: Date,
        min?: Date,
        default_month?: Date,
        openTo?: 'year' | 'month' | 'day',
        time?: boolean,
        key?: string,
        callback?: () => void,
      ) => {
        const openStart = openTo ? openTo : 'year';
        setModalCalendarYearData(state => {
          return {
            ...state,
            visible: true,
            openTo: openStart,
            type: type,
            date: {
              ...state.date,
              [type]: value ? value : null,
            },
            default: default_month,
            max: max ? max : new Date('9999-12-12'),
            min: min ? min : null,
            time: time === true ? true : false,
            key,
            callback,
          };
        });
      },
      setModalCalendarDataDate: (date_arr: Date[]) => {
        // const data = setAgoDate(date_type, gap);
        setModalCalendarYearData(state => {
          return {
            ...state,
            date: {
              start: date_arr[0],
              end: date_arr[1],
            },
          };
        });
      },
      setModalCalendarDataDateType: (date: Date, type: string) => {
        setModalCalendarYearData(state => {
          return {
            ...state,
            date: {
              ...state.date,
              [type]: date,
            },
          };
        });
      },
      setModalCalendarDataDateClear: () => {
        setModalCalendarYearData(state => {
          return {
            ...state,
            visible: false,
            date: {
              ...state.date,
              useDetailStart: null,
              useDetailEnd: null,
              allCheckStart: null,
              allCheckEnd: null,
              chargeDetailStart: null,
              chargeDetailEnd: null,
              checkStart: null,
              checkEnd: null,
              smsDetailStart: null,
              smsDetailEnd: null,
              smsReservationStart: null,
              smsReservationEnd: null,
              calculate_start: null,
              calculate_end: null,
              accountStatisticsStart: null,
              accountStatisticsEnd: null,
              counselRangeStart: null,
              counselRangeEnd: null,
              modify_startdate: null,
            },
          };
        });
      },
    },
  };

  return (
    <>
      <ModalContext.Provider value={modalController}>
        {children}
        <ModalConfirm />
        <ModalAlert />
        <ModalAlertOrigin />
        <ModalCheck />
        <ModalCalendar />
        <ModalCalendarStudent />
      </ModalContext.Provider>
    </>
  );
}

export default ModalProvider;
